import { useMemo } from "react";
import type { PublicMapModel } from "@fortex/api";
import type { GridColumn } from "@fortex/map";
import { useApp, useAuth } from "../../app/context";
import { features } from "../../auth/store";
import { CrudPage } from "../shared/CrudPage";
import { PublicMapForm, draftOfDetail, emptyPublicMapDraft, publicMapLink, updateOfDraft, validatePublicMapDraft } from "./PublicMapForm";
import type { PublicMapDraft } from "./PublicMapForm";

export function PublicMapsPage() {
  const { api, labels, config, lang } = useApp();
  const canEdit = useAuth(a => a.can(features.publicMap, "Full"));
  const dateFormat = useMemo(() => new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "hu-HU", { dateStyle: "short", timeStyle: "short" }), [lang]);

  const columns = useMemo<GridColumn<PublicMapModel>[]>(
    () => [
      { key: "Name", header: labels.publicMapName, field: "Name", sortable: true, filter: { kind: "text" }, render: row => row.name },
      { key: "Description", header: labels.publicMapDescription, field: "Description", sortable: true, filter: { kind: "text" }, render: row => row.description ?? "" },
      { key: "LayerCount", header: labels.publicMapLayerCount, field: "LayerCount", sortable: false, width: "110px", align: "end", render: row => row.layerCount },
      { key: "UpdatedDate", header: labels.publicMapUpdated, field: "UpdatedDate", sortable: true, width: "170px", filter: { kind: "date" }, render: row => dateFormat.format(new Date(row.updatedDate)) }
    ],
    [labels, dateFormat]
  );

  return (
    <CrudPage<PublicMapModel, PublicMapDraft>
      dataFxmap="public-maps-page"
      columns={columns}
      rowKey={row => row.id}
      load={(query, signal) => api.publicMaps(query, signal)}
      defaultSortField="Name"
      quickFields={["Name", "Description"]}
      canEdit={canEdit}
      newLabel={labels.publicMapCreate}
      emptyDraft={emptyPublicMapDraft}
      toDraft={async row => draftOfDetail(await api.publicMap(row.id))}
      detailTitle={editing => (editing.isNew ? labels.publicMapCreate : labels.publicMapEdit)}
      detailSubtitle={editing => (editing.draft.id === null ? null : editing.draft.name)}
      form={(draft, setDraft, errors) => <PublicMapForm api={api} labels={labels} draft={draft} setDraft={setDraft} errors={errors} readOnly={!canEdit} link={draft.id === null ? null : publicMapLink(config, draft.id)} />}
      validate={draft => validatePublicMapDraft(draft, labels)}
      save={draft => (draft.id === null ? api.createPublicMap(updateOfDraft(draft)) : api.updatePublicMap(draft.id, updateOfDraft(draft)))}
      remove={canEdit ? async draft => (draft.id === null ? undefined : api.deletePublicMap(draft.id)) : null}
      deleteText={draft => labels.publicMapDeleteText.replace("{name}", draft.name)}
      savedText={labels.publicMapSaved}
      deletedText={labels.publicMapDeleted}
      exportName="public-maps"
    />
  );
}
